import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import TrackingScripts from "@/components/TrackingScripts";
import Index from "./pages/Index";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";

const Historia = lazy(() => import("./pages/Historia"));
const Produtos = lazy(() => import("./pages/Produtos"));
const TrabalheConosco = lazy(() => import("./pages/TrabalheConosco"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const CookiesPage = lazy(() => import("./pages/Cookies"));
const Certificacoes = lazy(() => import("./pages/Certificacoes"));
const CompliancePage = lazy(() => import("./pages/Compliance"));
const Links = lazy(() => import("./pages/Links"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminResetPassword = lazy(() => import("./pages/admin/ResetPassword"));
const DashboardLayout = lazy(() => import("./pages/admin/DashboardLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProdutos = lazy(() => import("./pages/admin/AdminProdutos"));
const AdminCategorias = lazy(() => import("./pages/admin/AdminCategorias"));
const Usuarios = lazy(() => import("./pages/admin/Usuarios"));
const AdminGlossario = lazy(() => import("./pages/admin/AdminGlossario"));
const AdminCatalogo = lazy(() => import("./pages/admin/AdminCatalogo"));
const AdminEspecificacoes = lazy(() => import("./pages/admin/AdminEspecificacoes"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));
const AdminLinksBio = lazy(() => import("./pages/admin/AdminLinksBio"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <TrackingScripts />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" /></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/historia" element={<Historia />} />
            <Route path="/produtos" element={<Produtos />} />
            <Route path="/trabalhe-conosco" element={<TrabalheConosco />} />
            <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
            <Route path="/termos-de-uso" element={<TermosDeUso />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/qualidade/certificacoes" element={<Certificacoes />} />
            <Route path="/qualidade/compliance" element={<CompliancePage />} />
            <Route path="/links" element={<Links />} />
            {/* Admin routes */}
            <Route
              path="/admin/*"
              element={
                <AdminAuthProvider>
                  <Routes>
                    <Route path="login" element={<AdminLogin />} />
                    <Route path="reset-password" element={<AdminResetPassword />} />
                    <Route element={<DashboardLayout />}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="produtos" element={<AdminProdutos />} />
                      <Route path="categorias" element={<AdminCategorias />} />
                      <Route path="usuarios" element={<Usuarios />} />
                      <Route path="glossario" element={<AdminGlossario />} />
                      <Route path="catalogo" element={<AdminCatalogo />} />
                      <Route path="especificacoes" element={<AdminEspecificacoes />} />
                      <Route path="marketing/seo" element={<AdminMarketing />} />
                      <Route path="links-bio" element={<AdminLinksBio />} />
                    </Route>
                  </Routes>
                </AdminAuthProvider>
              }
            />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
